import { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";

const underlineStyle = {
    '& .MuiInput-underline:before': { borderBottomColor: '#dce3ea' },
    '& .MuiInput-underline:hover:before': { borderBottomColor: '#427898' },
    '& .MuiInput-underline:after': { borderBottomColor: '#427898' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#427898' },
};

function stripHtml(html) {
    return html ? html.replace(/<[^>]*>/g, '') : '';
}

// geo.admin.ch — Swiss Confederation official address API (free, no key required)
const SWISS_ADDRESS_API = 'https://api3.geo.admin.ch/rest/services/api/SearchServer';

export default function AddressAutocomplete({ customerData, setCustomerData, error, helperText }) {
    const [options, setOptions]   = useState([]);
    const [query, setQuery]       = useState('');
    const [loading, setLoading]   = useState(false);
    const debounceRef             = useRef(null);
    const controllerRef           = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!query || query.length < 3) {
            setOptions([]);
            return;
        }

        debounceRef.current = setTimeout(() => {
            if (controllerRef.current) controllerRef.current.abort();
            controllerRef.current = new AbortController();

            setLoading(true);

            const url = `${SWISS_ADDRESS_API}?sr=4326&type=locations&origins=address&searchText=${encodeURIComponent(query)}&limit=8&lang=de`;

            fetch(url, { signal: controllerRef.current.signal })
                .then(res => res.json())
                .then(data => {
                    const results = (data.results || []).map(item => {
                        const a = item.attrs || {};
                        const street    = a.street ? `${a.street}${a.num ? ' ' + a.num : ''}` : '';
                        const postalCode = a.postalcode || '';
                        const city       = a.city || a.label?.replace(/<[^>]*>/g, '').split(' ').slice(-1)[0] || '';
                        const label      = stripHtml(a.label || a.detail || '');
                        const formatted  = street
                            ? `${street}, ${postalCode} ${city}`.trim().replace(/,\s*$/, '')
                            : label;
                        return { label: formatted, street, postalCode, city };
                    });
                    setOptions(results);
                })
                .catch(e => { if (e.name !== 'AbortError') setOptions([]); })
                .finally(() => setLoading(false));
        }, 280);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query]);

    return (
        <Autocomplete
            freeSolo
            options={options}
            getOptionLabel={option => (typeof option === 'string' ? option : option.label || '')}
            filterOptions={x => x}
            loading={loading}
            onInputChange={(_, value) => setQuery(value)}
            onChange={(_, option) => {
                if (!option || typeof option === 'string') return;
                setCustomerData(prev => ({
                    ...prev,
                    address:    option.label,
                    street:     option.street,
                    postalCode: option.postalCode,
                    city:       option.city,
                    country:    'Switzerland',
                }));
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Address"
                    fullWidth
                    variant="standard"
                    value={customerData?.address || ''}
                    onChange={e => setCustomerData({ ...customerData, address: e.target.value })}
                    error={error}
                    helperText={helperText}
                    sx={underlineStyle}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading && <CircularProgress size={14} sx={{ color: '#427898' }} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}
